/* Collection-only enhancement. Native pagination remains the failure/no-JS path. */
(() => {
  if (customElements.get('gpp-progressive-products')) return;
  class GPPProgressiveProducts extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('#ProductGridContainer');
      this.grid = this.container?.querySelector('#product-grid');
      this.button = this.querySelector('[data-progressive-load]');
      this.status = this.querySelector('[data-progressive-status]');
      this.fallback = this.querySelector('[data-progressive-fallback]');
      this.section = this.dataset.sectionId;
      if (!location.pathname.includes('/collections/') || !this.grid || !this.section ||
          this.grid.dataset.id !== this.section || !this.button || !this.status || !this.fallback) return;
      this.life = {};
      this.page = Number(this.dataset.currentPage);
      this.initialPage = this.page;
      this.next = this.dataset.nextUrl || '';
      this.base = new URL(location.href);
      this.visited = new Set();
      this.busy = false;
      this.failed = false;
      this.near = false;
      this.permit = false;
      this.lastY = window.scrollY;
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.onClick = () => this.load(true);
      this.button.addEventListener('click', this.onClick);
      this.onScroll = () => {
        if (window.scrollY === this.lastY) return;
        this.lastY = window.scrollY;
        // Scroll while a request is pending cannot queue another page.
        if (this.busy || this.failed) return;
        this.permit = true;
        if (this.near) this.load(false);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.near = entries.some(entry => entry.isIntersecting);
          if (this.near && this.permit && !this.failed) this.load(false);
        }, { rootMargin: '900px 0px' });
        this.observer.observe(this);
      }
      this.render();
    }
    disconnectedCallback() {
      this.life = null;
      this.controller?.abort();
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      this.button?.removeEventListener('click', this.onClick);
    }
    signature(url) {
      const entries = [...url.searchParams].filter(([k, value]) => k !== 'page' && k !== 'section_id' && value !== '');
      return JSON.stringify(entries.sort(([a, b], [c, d]) => a.localeCompare(c) || b.localeCompare(d)));
    }
    pageURL(raw, expected) {
      const url = new URL(raw, this.base);
      if (url.origin !== this.base.origin || url.pathname !== this.base.pathname ||
          this.signature(url) !== this.signature(this.base) ||
          url.searchParams.getAll('page').length !== 1 ||
          !/^\d+$/.test(url.searchParams.get('page')) ||
          Number(url.searchParams.get('page')) !== expected || url.hash ||
          (url.searchParams.has('section_id') && url.searchParams.get('section_id') !== this.section)) {
        throw new Error('Invalid pagination destination');
      }
      url.searchParams.set('section_id', this.section);
      return url;
    }
    cards(grid) { return [...grid.children].filter(node => node.tagName === 'LI'); }
    key(card) {
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) throw new Error('Missing product identity');
      const url = new URL(link.getAttribute('href'), this.base);
      return url.pathname;
    }
    render() {
      this.button.hidden = !this.next;
      // Keep keyboard focus on the control while pending; load() guards repeats.
      this.button.setAttribute('aria-disabled', String(this.busy));
      this.button.textContent = this.failed ? 'Retry loading products' : 'Load more products';
      this.fallback.hidden = !this.failed;
      this.status.textContent = this.busy ? 'Loading more products…' : this.failed
        ? 'Products could not be loaded. Retry or use the page links.'
        : `${this.cards(this.grid).length} products shown.${this.next ? '' : (this.initialPage > 1 ? ' End of products.' : ' All products loaded.')}`;
    }
    async load(manual) {
      if (!this.life || this.busy || !this.next || (!manual && (!this.permit || this.failed))) return;
      if (!manual) {
        const bounds = this.getBoundingClientRect();
        if (bounds.top > innerHeight + 900 || bounds.bottom < -900) return;
      }
      const life = this.life;
      const focus = document.activeElement;
      this.busy = true;
      this.permit = false;
      this.failed = false;
      this.controller = new AbortController();
      this.render();
      try {
        const url = this.pageURL(this.next, this.page + 1);
        if (this.visited.has(url.href)) throw new Error('Repeated page');
        const response = await fetch(url.href, { signal: this.controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('HTTP failure');
        if (response.url && new URL(response.url).href !== url.href) throw new Error('Unexpected redirect');
        const html = await response.text();
        if (life !== this.life || !this.isConnected) return;
        if (location.pathname !== this.base.pathname || this.signature(new URL(location.href)) !== this.signature(this.base)) throw new Error('Collection changed');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const container = doc.querySelector('#ProductGridContainer');
        const grid = container?.querySelector('#product-grid');
        const pager = container?.querySelector('gpp-progressive-products');
        if (!grid || grid.dataset.id !== this.section || !pager || pager.dataset.sectionId !== this.section ||
            Number(pager.dataset.currentPage) !== this.page + 1 || !pager.hasAttribute('data-next-url') ||
            !pager.querySelector('[data-progressive-fallback]')) throw new Error('Malformed section');
        const next = pager.dataset.nextUrl || '';
        if (next) {
          const candidate = this.pageURL(next, this.page + 2);
          if (candidate.href === url.href || this.visited.has(candidate.href)) throw new Error('Pagination cycle');
        }
        const cards = this.cards(grid);
        if (!cards.length) throw new Error('Empty page');
        const keys = new Set(this.cards(this.grid).map(card => this.key(card)));
        const ids = new Set([...this.grid.querySelectorAll('[id]')].map(node => node.id));
        for (const card of cards) {
          const key = this.key(card);
          if (keys.has(key)) throw new Error('Duplicate product');
          keys.add(key);
          card.querySelectorAll('script').forEach(node => node.remove());
          for (const node of [card, ...card.querySelectorAll('[id]')]) {
            if (!node.id) continue;
            if (ids.has(node.id)) throw new Error('Duplicate control identity');
            ids.add(node.id);
          }
          card.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; img.decoding = 'async'; });
          [card, ...card.querySelectorAll('.scroll-trigger')].forEach(node => node.classList.add('scroll-trigger--cancel'));
        }
        const fragment = document.createDocumentFragment();
        cards.forEach(card => fragment.append(card));
        this.grid.append(fragment);
        this.visited.add(url.href);
        this.page += 1;
        this.next = next;
        this.dataset.currentPage = String(this.page);
        this.dataset.nextUrl = next;
        const fallback = pager.querySelector('[data-progressive-fallback]');
        fallback.querySelectorAll('script').forEach(node => node.remove());
        this.fallback.replaceChildren(...fallback.childNodes);
        if (manual && document.activeElement === focus && focus === this.button) {
          cards[0].querySelector('a[href*="/products/"]')?.focus({ preventScroll: true });
        }
      } catch (error) {
        if (life !== this.life || !this.isConnected) return;
        this.failed = true;
      } finally {
        if (life === this.life && this.isConnected) {
          this.busy = false;
          this.render();
        }
      }
    }
  }
  customElements.define('gpp-progressive-products', GPPProgressiveProducts);
})();
